import { useState, useEffect } from "react";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { 
  Target, 
  Users, 
  Calendar, 
  BarChart3, 
  Brain, 
  MessageSquare,
  Activity,
  Database,
  ChevronRight,
  Check,
  Clock,
  Award,
  BookOpen,
  Loader2,
  Menu,
  X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// EmailJS Configuration
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_mentoria";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_lead";
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "";

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToForm = () => {
    document.getElementById("form-section")?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-slate-950/95 glass-effect border-b border-emerald-500/20"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
        <div className="font-cabinet font-bold text-xl text-white tracking-tight flex items-center gap-2">
          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          MENTORIA
        </div>
        
        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          <a href="#objetivo" className="text-slate-300 hover:text-emerald-400 transition-colors text-sm">
            Objetivo
          </a>
          <a href="#como-funciona" className="text-slate-300 hover:text-emerald-400 transition-colors text-sm">
            Como Funciona
          </a>
          <a href="#conteudo" className="text-slate-300 hover:text-emerald-400 transition-colors text-sm">
            Conteúdo
          </a>
          <a href="#professor" className="text-slate-300 hover:text-emerald-400 transition-colors text-sm">
            Professor
          </a>
          <Button
            data-testid="header-cta-button"
            onClick={scrollToForm}
            className="bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold"
          >
            Solicitar Participação
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-button"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-950/98 glass-effect border-t border-emerald-500/20 animate-fade-in">
          <nav className="flex flex-col px-6 py-4 gap-4">
            <a href="#objetivo" onClick={() => setMobileMenuOpen(false)} className="text-slate-300 hover:text-emerald-400 transition-colors py-2">
              Objetivo
            </a>
            <a href="#como-funciona" onClick={() => setMobileMenuOpen(false)} className="text-slate-300 hover:text-emerald-400 transition-colors py-2">
              Como Funciona
            </a>
            <a href="#conteudo" onClick={() => setMobileMenuOpen(false)} className="text-slate-300 hover:text-emerald-400 transition-colors py-2">
              Conteúdo
            </a>
            <a href="#professor" onClick={() => setMobileMenuOpen(false)} className="text-slate-300 hover:text-emerald-400 transition-colors py-2">
              Professor
            </a>
            <Button
              onClick={scrollToForm}
              className="bg-emerald-500 text-slate-950 hover:bg-emerald-400 w-full font-semibold"
            >
              Solicitar Participação
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToForm = () => {
    document.getElementById("form-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image - Soccer field aerial view */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1607414721186-5309963d7b52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHRhY3RpY3MlMjBib2FyZCUyMGFuYWx5c2lzJTIwd2hpdGVib2FyZHxlbnwwfHx8fDE3NzUzMjM3MTR8MA&ixlib=rb-4.1.0&q=85')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-950/70 to-slate-950" />
      
      {/* Accent line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-500 to-transparent" />
      
      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center pt-20">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-8 animate-fade-in">
          <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          <span className="text-xs tracking-[0.2em] uppercase text-emerald-400 font-semibold">
            Performance no Futebol
          </span>
        </div>
        <h1 className="font-cabinet text-4xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tighter leading-none mb-8 animate-fade-in-up">
          Mentoria – Gestão de{" "}
          <span className="text-gradient">Performance</span>{" "}
          no Futebol
        </h1>
        <p className="text-slate-300 text-base md:text-lg max-w-3xl mx-auto leading-relaxed mb-6 opacity-0 animate-fade-in-up animation-delay-200">
          Ao longo dos anos, dentro e fora do Brasil, estudando e vivendo o futebol na prática, 
          fui construindo uma forma de enxergar e organizar o trabalho em um ambiente complexo, 
          dinâmico e extremamente exigente.
        </p>
        <p className="text-white text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed mb-10 font-medium opacity-0 animate-fade-in-up animation-delay-300">
          Essa mentoria nasce disso.
        </p>
        <p className="text-slate-400 text-base max-w-2xl mx-auto leading-relaxed mb-12 opacity-0 animate-fade-in-up animation-delay-400">
          Não para dizer o que é certo ou errado, mas para compartilhar experiências reais 
          com quem está no caminho ou quer entrar no futebol.
        </p>
        <Button
          data-testid="hero-cta-button"
          onClick={scrollToForm}
          size="lg"
          className="bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-bold px-10 py-7 text-base opacity-0 animate-fade-in-up animation-delay-500 glow-accent"
        >
          Solicitar participação
          <ChevronRight className="ml-2 h-5 w-5" />
        </Button>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-emerald-500/50 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-emerald-400 rounded-full" />
        </div>
      </div>
    </section>
  );
};

// Objective Section
const ObjectiveSection = () => {
  const objectives = [
    { icon: Target, text: "Entender melhor a realidade do futebol" },
    { icon: Users, text: "Se organizar dentro de um ambiente exigente e desafiador" },
    { icon: Brain, text: "Desenvolver sua própria forma de trabalhar" },
    { icon: ChevronRight, text: "Evoluir com mais clareza e direção" },
  ];

  return (
    <section
      id="objetivo"
      data-testid="objective-section"
      className="py-24 md:py-32 bg-slate-900"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-12 bg-emerald-500" />
          <p className="text-xs tracking-[0.2em] uppercase text-emerald-400 font-bold">
            Objetivo
          </p>
        </div>
        <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight mb-12">
          A mentoria foi pensada para quem quer:
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {objectives.map((item, index) => (
            <div
              key={index}
              className="bg-slate-800/50 border border-slate-700 rounded-xl p-8 hover:border-emerald-500/50 hover:bg-slate-800/80 transition-all duration-300 group"
            >
              <item.icon className="h-6 w-6 text-emerald-400 group-hover:scale-110 transition-transform mb-4" />
              <p className="text-slate-200 text-lg">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  return (
    <section
      id="como-funciona"
      data-testid="how-it-works-section"
      className="py-24 md:py-32 bg-slate-950"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-12 bg-emerald-500" />
          <p className="text-xs tracking-[0.2em] uppercase text-emerald-400 font-bold">
            Estrutura
          </p>
        </div>
        <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight mb-12">
          Como Funciona
        </h2>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div className="space-y-6">
            <p className="text-slate-200 text-lg leading-relaxed">
              A mentoria será estruturada em <span className="text-emerald-400 font-semibold">7 encontros guiados</span>, 
              com temas definidos e foco em aplicação prática.
            </p>
            <p className="text-slate-400 leading-relaxed">
              Os encontros acontecerão durante o período de Data FIFA / Copa do Mundo, 
              com frequência de duas vezes por semana.
            </p>

            <div className="bg-slate-800/50 border border-emerald-500/30 rounded-xl p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-emerald-400" />
                <span className="text-slate-200">Terças e quintas-feiras</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-emerald-400" />
                <span className="text-slate-200">Das 20h às 21h45</span>
              </div>
            </div>

            <p className="text-slate-400 leading-relaxed">
              Além dos 7 encontros, haverá um <span className="text-slate-200">momento individual</span> ao final 
              da mentoria, destinado à discussão de dúvidas e consolidação do aprendizado.
              <span className="text-emerald-400 font-medium"> Este encontro será gravado.</span>
            </p>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-emerald-400" />
                <span className="text-slate-200">Plataforma Hotmart</span>
              </div>
              <div className="flex items-center gap-3">
                <BookOpen className="h-5 w-5 text-emerald-400" />
                <span className="text-slate-200">Material compartilhado com os alunos</span>
              </div>
              <p className="text-slate-400 text-sm pl-8">
                Os encontros não serão gravados, mas todo material utilizado será disponibilizado.
              </p>
            </div>

            <div className="pt-4 border-t border-slate-700">
              <p className="text-slate-300 italic">
                Não se trata de um curso engessado. É uma proposta que equilibra organização, 
                método e experiência prática.
              </p>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-xl blur-xl" />
            <img
              src="https://images.unsplash.com/photo-1704716503391-eb4cc4d08372?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMHNvY2NlciUyMHRyYWluaW5nJTIwZmllbGQlMjBhdGhsZXRlcyUyMHJ1bm5pbmd8ZW58MHx8fHwxNzc1MzIzNzE0fDA&ixlib=rb-4.1.0&q=85"
              alt="Treino de futebol"
              className="relative rounded-xl w-full"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Management System Section
const ManagementSystemSection = () => {
  const features = [
    "SWC individual",
    "Dados bioquímicos",
    "Controle de lesões",
    "Disponibilidade do atleta",
    "Calendário congestionado",
    "Minutagem",
    "Perfis físicos",
  ];

  return (
    <section
      data-testid="management-system-section"
      className="py-24 md:py-32 bg-slate-900"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1 relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-xl blur-xl" />
            <img
              src="https://images.unsplash.com/photo-1774344336133-0823eba3434b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxzb2NjZXIlMjBwbGF5ZXJzJTIwdHJhaW5pbmclMjBzZXNzaW9uJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3NTMyMzcxNHww&ixlib=rb-4.1.0&q=85"
              alt="Atletas em treino"
              className="relative rounded-xl w-full"
            />
          </div>
          
          <div className="order-1 lg:order-2 space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-emerald-500" />
              <p className="text-xs tracking-[0.2em] uppercase text-emerald-400 font-bold">
                Sistema de Gestão
              </p>
            </div>
            <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight">
              Você não vai só aprender — <span className="text-gradient">vai aplicar.</span>
            </h2>
            <p className="text-slate-400 leading-relaxed">
              Durante a mentoria, você terá acesso ao software utilizado no futebol profissional 
              para controle e gestão de atletas.
            </p>

            <div className="grid grid-cols-2 gap-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-400" />
                  <span className="text-slate-300 text-sm">{feature}</span>
                </div>
              ))}
            </div>

            <div className="pt-6 border-t border-slate-700 space-y-3">
              <p className="text-slate-200 font-medium">Além disso:</p>
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4 text-emerald-400" />
                <span className="text-slate-400 text-sm">Transformar dados em decisão</span>
              </div>
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-emerald-400" />
                <span className="text-slate-400 text-sm">Organizar informação de forma funcional</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Mentorship Content Section
const MentorshipContentSection = () => {
  const modules = [
    {
      title: "Controle de carga nos microciclos",
      items: [
        "Organização de microciclos com 1, 2 e 3 jogos por semana",
        "Ajuste de carga em função do calendário",
        "Relação treino–jogo dentro da semana",
        "Distribuição de estímulos (neuromuscular, metabólico e estrutural)",
      ],
    },
    {
      title: "Controle de fadiga (aguda e tardia)",
      items: [
        "Integração: Bioquímica, Testes neuromusculares (CMJ, IMTP, ISO30), VFC, Termografia",
        "Identificação e gestão da fadiga aguda e tardia",
        "Interpretação prática dos dados",
        "Relação com risco, desempenho e tomada de decisão",
      ],
    },
    {
      title: "Gestão e integração de staff",
      items: [
        "Organização do fluxo de informação",
        "Integração entre áreas (físico, médico e comissão)",
        "Papel da performance dentro do contexto do clube",
        "Tomada de decisão compartilhada",
      ],
    },
    {
      title: "Rotinas de performance no futebol de elite",
      items: [
        "Rotinas pré, durante e pós-jogo",
        "Organização semanal do trabalho",
        "Estratégias de recuperação",
        "Ajustes em função do calendário",
      ],
    },
    {
      title: "Comunicação e mentalidade",
      items: [
        "Comunicação dentro do staff",
        "Relação com atletas",
        "Clareza na tomada de decisão",
        "Mentalidade compatível com a exigência do futebol",
      ],
    },
    {
      title: "Organização e gestão da informação",
      items: [
        "Estruturação de dados no dia a dia",
        "Organização da informação de forma utilizável",
        "Leitura de tendência e mudança (SWC)",
        "Aplicação prática do ecossistema que envolve a saúde e performance",
      ],
    },
  ];

  return (
    <section
      id="conteudo"
      data-testid="mentorship-content-section"
      className="py-24 md:py-32 bg-slate-950"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-12 bg-emerald-500" />
          <p className="text-xs tracking-[0.2em] uppercase text-emerald-400 font-bold">
            Currículo
          </p>
        </div>
        <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight mb-6">
          Conteúdo da Mentoria
        </h2>
        <p className="text-slate-400 max-w-3xl mb-12 leading-relaxed">
          A mentoria é estruturada a partir de situações reais do dia a dia do futebol, 
          com foco em como organizar, interpretar e tomar decisão dentro do contexto profissional.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map((module, index) => (
            <div
              key={index}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-emerald-500/50 transition-all duration-300 group"
            >
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <span className="text-emerald-400 font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
              </div>
              <h3 className="font-cabinet text-lg font-semibold text-white mb-4">
                {module.title}
              </h3>
              <ul className="space-y-2">
                {module.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="text-slate-400 text-sm flex items-start gap-2">
                    <span className="text-emerald-500 mt-1">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 p-6 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl">
          <p className="text-slate-200 text-center">
            O foco não está na ferramenta, mas em como <span className="text-emerald-400 font-medium">organizar, interpretar e tomar decisão</span> dentro do contexto real do futebol.
          </p>
        </div>
      </div>
    </section>
  );
};

// About Professor Section
const AboutProfessorSection = () => {
  const achievements = [
    { icon: Award, text: "Libertadores, Brasileiro, Copa do Brasil, Supercopa, Mundial" },
    { icon: BookOpen, text: "Mestre e Doutor pela UFRJ" },
    { icon: Users, text: "Professor da CBF Academy (Licenças A e B)" },
    { icon: Activity, text: "Experiência no SL Benfica Health & Performance Congress" },
  ];

  return (
    <section
      id="professor"
      data-testid="professor-section"
      className="py-24 md:py-32 bg-slate-900"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-12 bg-emerald-500" />
              <p className="text-xs tracking-[0.2em] uppercase text-emerald-400 font-bold">
                Sobre o Professor
              </p>
            </div>
            <p className="text-slate-200 text-lg leading-relaxed">
              Mestre e Doutor pela UFRJ, atua no futebol de alto rendimento com foco em 
              controle de carga, fadiga neuromuscular e tomada de decisão aplicada à performance.
            </p>
            <p className="text-slate-400 leading-relaxed">
              Com experiência direta em ambientes de elite, participou de conquistas como 
              Libertadores, Campeonato Brasileiro, Copa do Brasil, Supercopa, títulos estaduais 
              e Mundial (Intercontinental).
            </p>
            <p className="text-slate-400 leading-relaxed">
              Possui formação e vivência internacional, com participação no SL Benfica Health 
              & Performance Congress e imersões na Europa ao lado de profissionais de referência mundial.
            </p>
            <p className="text-slate-400 leading-relaxed">
              Autor de artigos científicos em ciência do esporte, conectando evidência e 
              prática no dia a dia do futebol. Professor da CBF Academy, atuando na formação 
              de treinadores nas licenças A e B.
            </p>
            <p className="text-slate-200 leading-relaxed font-medium">
              Hoje, traduz anos de experiência prática e conhecimento científico em métodos 
              aplicáveis para profissionais que querem atuar — e se destacar — no alto nível.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-6">
              {achievements.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <item.icon className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-400 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 rounded-xl blur-xl" />
            <img
              src="https://customer-assets.emergentagent.com/job_gestao-futebol/artifacts/q68psclh_DDBEC4C6-E9DB-4E87-B99F-131782D2F2BC_1_105_c.jpeg"
              alt="Professor"
              className="relative rounded-xl w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Participation Process Section
const ParticipationProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Solicitação",
      description: "Preencha o formulário com seus dados profissionais",
    },
    {
      number: "02",
      title: "Orientações",
      description: "Receba as instruções para confirmação da participação",
    },
    {
      number: "03",
      title: "Confirmação",
      description: "Garanta sua vaga mediante confirmação e pagamento",
    },
  ];

  return (
    <section
      data-testid="participation-process-section"
      className="py-24 md:py-32 bg-slate-950"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-px w-12 bg-emerald-500" />
          <p className="text-xs tracking-[0.2em] uppercase text-emerald-400 font-bold">
            Processo
          </p>
        </div>
        <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight mb-6">
          Como Participar
        </h2>
        <p className="text-slate-400 max-w-3xl mb-12 leading-relaxed">
          A participação na mentoria ocorre mediante solicitação, devido ao número limitado de vagas. 
          O grupo será intencionalmente reduzido (máximo de <span className="text-emerald-400 font-semibold">7 participantes</span>), 
          permitindo maior profundidade nas discussões e aplicação prática.
        </p>

        <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-6 mb-12">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-1">
              <Clock className="h-4 w-4 text-amber-400" />
            </div>
            <div>
              <p className="text-amber-400 font-semibold mb-2">Atenção: Vagas Limitadas</p>
              <p className="text-slate-300 text-sm leading-relaxed">
                São apenas <span className="text-white font-bold">7 vagas</span> disponíveis. Ao solicitar participação, 
                você confirma sua intenção de realizar o pagamento. Após o recebimento do link de pagamento, 
                você terá <span className="text-amber-400 font-bold">12 horas</span> para efetuar a transação, 
                caso contrário perderá o direito à vaga.
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {steps.map((step, index) => (
            <div
              key={index}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-8 hover:border-emerald-500/50 transition-all duration-300 relative group"
            >
              <span className="text-6xl font-cabinet font-bold text-emerald-500/20 group-hover:text-emerald-500/30 transition-colors absolute top-4 right-4">
                {step.number}
              </span>
              <h3 className="font-cabinet text-xl font-semibold text-white mb-3 relative z-10">
                {step.title}
              </h3>
              <p className="text-slate-400 text-sm relative z-10">{step.description}</p>
            </div>
          ))}
        </div>

        <div className="space-y-4 text-slate-400 text-sm border-l-2 border-emerald-500/50 pl-6">
          <p>
            A definição dos participantes não é baseada em critérios subjetivos, 
            mas sim na ordem de confirmação e disponibilidade de vagas.
          </p>
          <p>
            <span className="text-amber-400 font-medium">Importante:</span> Ao solicitar participação, você confirma 
            sua intenção de efetuar o pagamento. O link será enviado e você terá 12 horas para concluir a transação.
          </p>
          <p>
            Caso as vagas sejam preenchidas, os próximos interessados poderão ser 
            direcionados para uma nova turma.
          </p>
        </div>
      </div>
    </section>
  );
};

// Lead Form Section
const LeadFormSection = () => {
  const [formData, setFormData] = useState({
    nome_completo: "",
    email: "",
    idade: "",
    profissao: "",
    area_atuacao: "",
    tempo_experiencia: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, tempo_experiencia: value }));
  };

  // Map experience values to readable text
  const getExperienceText = (value) => {
    const experienceMap = {
      "menos-1-ano": "Menos de 1 ano",
      "1-3-anos": "1 a 3 anos",
      "3-5-anos": "3 a 5 anos",
      "5-10-anos": "5 a 10 anos",
      "mais-10-anos": "Mais de 10 anos",
    };
    return experienceMap[value] || value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const leadData = {
      nome_completo: formData.nome_completo,
      email: formData.email,
      idade: formData.idade,
      profissao: formData.profissao,
      area_atuacao: formData.area_atuacao,
      tempo_experiencia: getExperienceText(formData.tempo_experiencia),
      data_solicitacao: new Date().toLocaleString("pt-BR"),
    };

    let dbSaved = false;
    let emailSent = false;

    // 1. PRIMEIRO: Salvar no banco de dados (com retry)
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await axios.post(`${API}/leads`, {
          ...formData,
          idade: parseInt(formData.idade),
        });
        console.log(`Lead saved to database (attempt ${attempt}):`, response.data);
        dbSaved = true;
        break;
      } catch (dbError) {
        console.log(`Database error (attempt ${attempt}):`, dbError.message);
        if (attempt < 3) {
          await new Promise(r => setTimeout(r, 1000)); // Wait 1 second before retry
        }
      }
    }

    // 2. SEGUNDO: Enviar email via FormSubmit
    try {
      const formSubmitData = {
        _subject: `Nova Solicitacao Mentoria: ${formData.nome_completo}`,
        _template: "table",
        _captcha: "false",
        _replyto: formData.email,
        "Nome Completo": leadData.nome_completo,
        "Email do Interessado": leadData.email,
        "Idade": `${leadData.idade} anos`,
        "Profissao": leadData.profissao,
        "Area de Atuacao": leadData.area_atuacao,
        "Tempo de Experiencia": leadData.tempo_experiencia,
        "Data da Solicitacao": leadData.data_solicitacao,
        "Salvo no Banco": dbSaved ? "Sim" : "NAO - VERIFICAR",
      };

      const formResponse = await fetch("https://formsubmit.co/ajax/pedromenezes5@hotmail.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(formSubmitData),
      });
      
      const formResult = await formResponse.json();
      console.log("FormSubmit response:", formResult);
      
      if (formResult.success === "true") {
        emailSent = true;
        console.log("Email sent successfully via FormSubmit!");
      }
    } catch (formError) {
      console.log("FormSubmit error:", formError.message);
    }

    // 3. Mostrar resultado ao usuário
    if (dbSaved || emailSent) {
      setIsSubmitted(true);
      toast.success("Solicitação enviada com sucesso!");
    } else {
      toast.error("Erro ao enviar. Por favor, tente novamente.");
    }

    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <section
        id="form-section"
        data-testid="lead-form-section"
        className="py-24 md:py-32 bg-slate-900"
      >
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-slate-950/50 border border-emerald-500/30 rounded-xl p-12">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 glow-accent">
              <Check className="h-8 w-8 text-slate-950" />
            </div>
            <h3 className="font-cabinet text-2xl font-semibold text-white mb-4">
              Solicitação Enviada
            </h3>
            <p className="text-slate-400">
              Em breve você receberá as orientações para confirmação da sua participação.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="form-section"
      data-testid="lead-form-section"
      className="py-24 md:py-32 bg-slate-900"
    >
      <div className="max-w-2xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-6">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            <span className="text-xs tracking-[0.2em] uppercase text-emerald-400 font-semibold">
              Inscrição
            </span>
          </div>
          <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight mb-4">
            Solicitar Participação
          </h2>
          <p className="text-slate-400">
            Preencha o formulário abaixo para solicitar sua participação na mentoria.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-slate-950/50 border border-slate-800 rounded-xl p-8 space-y-6"
          data-testid="lead-form"
        >
          <div className="space-y-2">
            <Label htmlFor="nome_completo" className="text-slate-300">
              Nome completo *
            </Label>
            <Input
              id="nome_completo"
              name="nome_completo"
              value={formData.nome_completo}
              onChange={handleChange}
              required
              className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Seu nome completo"
              data-testid="input-nome"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-300">
              Email *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="seu@email.com"
              data-testid="input-email"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="idade" className="text-slate-300">
                Idade *
              </Label>
              <Input
                id="idade"
                name="idade"
                type="number"
                min="18"
                max="99"
                value={formData.idade}
                onChange={handleChange}
                required
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="25"
                data-testid="input-idade"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="profissao" className="text-slate-300">
                Profissão *
              </Label>
              <Input
                id="profissao"
                name="profissao"
                value={formData.profissao}
                onChange={handleChange}
                required
                className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder="Preparador Físico"
                data-testid="input-profissao"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="area_atuacao" className="text-slate-300">
              Área de atuação *
            </Label>
            <Input
              id="area_atuacao"
              name="area_atuacao"
              value={formData.area_atuacao}
              onChange={handleChange}
              required
              className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Futebol profissional, Base, Academia..."
              data-testid="input-area"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="tempo_experiencia" className="text-slate-300">
              Tempo de experiência no futebol ou esporte *
            </Label>
            <Select
              value={formData.tempo_experiencia}
              onValueChange={handleSelectChange}
              required
            >
              <SelectTrigger 
                className="bg-slate-900 border-slate-700 text-white focus:ring-emerald-500 focus:border-emerald-500"
                data-testid="select-experiencia"
              >
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="menos-1-ano">Menos de 1 ano</SelectItem>
                <SelectItem value="1-3-anos">1 a 3 anos</SelectItem>
                <SelectItem value="3-5-anos">3 a 5 anos</SelectItem>
                <SelectItem value="5-10-anos">5 a 10 anos</SelectItem>
                <SelectItem value="mais-10-anos">Mais de 10 anos</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <p className="text-amber-400 text-xs leading-relaxed">
              <span className="font-bold">Importante:</span> Ao enviar esta solicitação, você confirma sua intenção de efetuar o pagamento. 
              Após receber o link, terá <span className="font-bold">12 horas</span> para concluir a transação ou perderá o direito à vaga.
            </p>
          </div>

          <Button
            type="submit"
            disabled={isSubmitting || !formData.tempo_experiencia}
            className="w-full bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-bold py-6 glow-accent"
            data-testid="lead-form-submit"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              "Solicitar participação"
            )}
          </Button>
        </form>
      </div>
    </section>
  );
};

// Closing Section
const ClosingSection = () => {
  const scrollToForm = () => {
    document.getElementById("form-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="closing-section"
      className="py-24 md:py-32 bg-slate-950 relative overflow-hidden"
    >
      {/* Background accent */}
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 to-transparent" />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
        <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight mb-6">
          Mais do que aprender, é <span className="text-gradient">construir</span>
        </h2>
        <p className="text-slate-400 text-lg mb-8 max-w-2xl mx-auto">
          Essa mentoria não é sobre um modelo pronto. É sobre ajudar você a construir o seu.
        </p>

        <div className="bg-slate-900/50 border border-emerald-500/30 rounded-xl p-8 mb-10 inline-block">
          <p className="text-slate-500 text-sm mb-2">Investimento total</p>
          <p className="font-cabinet text-5xl font-bold text-gradient">R$ 1.500</p>
          <p className="text-emerald-400 text-sm mt-3 font-medium">
            ou até 10x de R$ 150 no cartão de crédito
          </p>
        </div>

        <div>
          <Button
            onClick={scrollToForm}
            size="lg"
            className="bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-bold px-12 py-7 text-base glow-accent"
            data-testid="closing-cta-button"
          >
            Solicitar participação
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-8 bg-slate-950 border-t border-slate-800">
      <div className="max-w-6xl mx-auto px-6 md:px-12 text-center">
        <p className="text-slate-600 text-sm">
          © {new Date().getFullYear()} Mentoria – Gestão de Performance no Futebol
        </p>
        {/* Link discreto para área administrativa - só aparece como ponto */}
        <a 
          href="/admin/leads" 
          className="inline-block mt-4 w-2 h-2 bg-slate-800 hover:bg-emerald-500 rounded-full transition-colors"
          title=""
        />
      </div>
    </footer>
  );
};

// Main Landing Page
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <HeroSection />
      <ObjectiveSection />
      <HowItWorksSection />
      <ManagementSystemSection />
      <MentorshipContentSection />
      <AboutProfessorSection />
      <ParticipationProcessSection />
      <LeadFormSection />
      <ClosingSection />
      <Footer />
    </div>
  );
};

export default LandingPage;
